/**
 * Rounds the corners of a 2D profile cross-section.
 *
 * Extruded aluminium comes out of a die with a small radius on every corner:
 * that radius is what draws the thin travelling highlight along a profile as
 * the case turns. A mathematically sharp corner cannot catch one, which is why
 * sharp sections read as CAD output no matter how good the lighting is.
 *
 * Each corner is replaced by a quadratic Bézier through the two tangent points,
 * which approximates a fillet closely enough at these radii (a few tenths of a
 * millimetre) and stays well-defined for concave corners and awkward angles
 * alike. Segments are chosen so each turn stays under the crease angle used by
 * {@link ProfiledContourGeometry}, so the result shades as a curve rather than
 * as a run of facets.
 */

import { PART_CREASE_ANGLE_DEG } from "@project/emerge/src/util/creasedNormals";

/** Corner radius in millimetres. */
export const PROFILE_CORNER_RADIUS = 0.4;

/** Straight runs shorter than this keep their sharp corners. */
const MIN_EDGE_LENGTH = 0.05;

/** Corners flatter than this are left alone — they are not corners. */
const MIN_TURN_RADIANS = (8 * Math.PI) / 180;

/**
 * Start with the existing four chords, including their exact sample points.
 *
 * A right angle's largest joint is 28.1°, but an acute tooth can exceed 46°.
 * Refine each corner independently; never soften the global crease threshold
 * or change its tangent points to hide an under-tessellated curve.
 */
const BASE_SEGMENTS = 4;
// Pathological near-cusps can demand unbounded angular resolution. Keep their
// original curve/tangent points rather than changing the radius at this limit.
const MAX_SEGMENTS = 64;
const MAX_CHORD_TURN = ((PART_CREASE_ANGLE_DEG - 1) * Math.PI) / 180;
const MAX_CHORD_ERROR_MM = 0.01;

type Point = [number, number];

const subtract = (a: Point, b: Point): Point => [a[0] - b[0], a[1] - b[1]];
const length = (v: Point): number => Math.hypot(v[0], v[1]);

const normalize = (v: Point): Point => {
  const magnitude = length(v);
  return magnitude === 0 ? [0, 0] : [v[0] / magnitude, v[1] / magnitude];
};

/** Turn angle between two unit direction vectors, in radians. */
const turnAngle = (from: Point, to: Point): number => {
  const dot = Math.min(1, Math.max(-1, from[0] * to[0] + from[1] * to[1]));
  return Math.acos(dot);
};

const quadraticBezier = (
  start: Point,
  control: Point,
  end: Point,
  t: number,
): Point => {
  const inverse = 1 - t;
  const a = inverse * inverse;
  const b = 2 * inverse * t;
  const c = t * t;
  return [
    a * start[0] + b * control[0] + c * end[0],
    a * start[1] + b * control[1] + c * end[1],
  ];
};

function sampleCorner(
  start: Point,
  control: Point,
  end: Point,
  incoming: Point,
  outgoing: Point,
  cut: number,
): Point[] {
  // For a quadratic, the maximum deviation from each equal-t chord is its
  // midpoint error: |start - 2*control + end| / (4 * segments²). Bound that
  // in millimetres as well as relative to small, edge-clamped fillets.
  const curvature = length([
    start[0] - control[0] + end[0] - control[0],
    start[1] - control[1] + end[1] - control[1],
  ]);
  const allowedError = Math.min(MAX_CHORD_ERROR_MM, cut / 32);
  for (let segments = BASE_SEGMENTS; ; segments *= 2) {
    const samples: Point[] = [start];
    for (let step = 1; step < segments; step++) {
      samples.push(quadraticBezier(start, control, end, step / segments));
    }
    samples.push(end);

    let previous = start;
    let direction = incoming;
    let largestTurn = 0;
    for (const point of samples.slice(1)) {
      const nextDirection = normalize(subtract(point, previous));
      largestTurn = Math.max(largestTurn, turnAngle(direction, nextDirection));
      direction = nextDirection;
      previous = point;
    }
    // Include the joins to both straight runs, not just interior fillet joints.
    largestTurn = Math.max(largestTurn, turnAngle(direction, outgoing));
    const chordError = curvature / (4 * segments * segments);
    if (
      segments === MAX_SEGMENTS ||
      (largestTurn < MAX_CHORD_TURN && chordError <= allowedError)
    ) {
      return samples;
    }
  }
}

/**
 * Indexes a closed polygon, wrapping past either end. The polygon is non-empty
 * by the time this is reached, so the throw only records that precondition —
 * it is what lets the corner walk read its neighbours without a type assertion.
 */
const atWrapped = (points: Point[], index: number): Point => {
  const point =
    points[((index % points.length) + points.length) % points.length];
  if (!point) {
    throw new Error("roundProfileCorners: cannot index an empty polygon");
  }
  return point;
};

/**
 * @param points Closed polygon, without a repeated final point.
 * @param radius Corner radius; clamped per corner to the adjacent edges.
 */
export function roundProfileCorners(
  points: Point[],
  radius = PROFILE_CORNER_RADIUS,
): Point[] {
  if (points.length >= 3 && radius > 0) {
    const rounded: Point[] = [];

    for (const [i, current] of points.entries()) {
      const previous = atWrapped(points, i - 1);
      const next = atWrapped(points, i + 1);

      const incoming = subtract(current, previous);
      const outgoing = subtract(next, current);
      const incomingLength = length(incoming);
      const outgoingLength = length(outgoing);

      if (
        incomingLength < MIN_EDGE_LENGTH ||
        outgoingLength < MIN_EDGE_LENGTH
      ) {
        rounded.push(current);
        continue;
      }

      const incomingDirection = normalize(incoming);
      const outgoingDirection = normalize(outgoing);

      const turn = turnAngle(incomingDirection, outgoingDirection);
      if (turn < MIN_TURN_RADIANS || Math.PI - turn < 1e-6) {
        // A reversal's equal-cut endpoints coincide. Keep the original cusp
        // instead of emitting a folded arc that runs out and back on itself.
        rounded.push(current);
        continue;
      }

      // Never consume more than half of either adjacent edge, so neighbouring
      // corners on a short segment cannot overrun each other.
      const cut = Math.min(radius, incomingLength / 2, outgoingLength / 2);

      const start: Point = [
        current[0] - incomingDirection[0] * cut,
        current[1] - incomingDirection[1] * cut,
      ];
      const end: Point = [
        current[0] + outgoingDirection[0] * cut,
        current[1] + outgoingDirection[1] * cut,
      ];

      rounded.push(
        ...sampleCorner(
          start,
          current,
          end,
          incomingDirection,
          outgoingDirection,
          cut,
        ),
      );
    }

    return rounded;
  }

  return points;
}
